import { useState, useEffect } from 'react';
import { opsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Shield, Zap, Globe, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Activity, Server, Pause, OctagonX,
} from 'lucide-react';
import { toast } from 'sonner';

export default function OpsCenterPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await opsAPI.center();
      setData(res.data);
    } catch (e) { toast.error('Ops data alinamadi'); }
    setLoading(false);
  };

  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, []);

  const handleKillSwitch = async () => {
    if (!window.confirm('KILL SWITCH: Tum gonderimler durdurulacak. Emin misiniz?')) return;
    const res = await opsAPI.killSwitch();
    toast.error(`KILL SWITCH: ${res.data.domains_paused} domain, ${res.data.jobs_cancelled} job, ${res.data.campaigns_stopped} kampanya durduruldu`);
    load();
  };

  const handleDrain = async () => {
    if (!window.confirm('Tum queue bosaltilacak. Emin misiniz?')) return;
    const res = await opsAPI.drainQueue();
    toast.success(`${res.data.cancelled} job iptal edildi`);
    load();
  };

  const handleAdjustWarmup = async () => {
    const res = await opsAPI.adjustWarmup();
    toast.success(`${res.data.adjusted} domain ayarlandi`);
    load();
  };

  const handleBackup = async () => {
    const res = await opsAPI.createBackup();
    toast.success(`Snapshot olusturuldu: ${res.data.domains} domain, ${res.data.nodes} node`);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  const sys = data?.system || {};
  const q = data?.queue?.total || {};

  return (
    <div className="space-y-6" data-testid="ops-center">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Operations Center</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={sys.environment === 'production' ? 'default' : 'secondary'}>{sys.environment}</Badge>
            <span className="text-sm text-muted-foreground">Realtime Ops Dashboard</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleBackup}><Shield className="w-4 h-4 mr-1" /> Snapshot</Button>
          <Button variant="outline" size="sm" onClick={handleAdjustWarmup}><Zap className="w-4 h-4 mr-1" /> Warmup Ayarla</Button>
          <Button variant="outline" size="sm" onClick={handleDrain}><Pause className="w-4 h-4 mr-1" /> Queue Drain</Button>
          <Button variant="destructive" size="sm" onClick={handleKillSwitch}><OctagonX className="w-4 h-4 mr-1" /> KILL SWITCH</Button>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /></Button>
        </div>
      </div>

      {/* Alerts */}
      {data?.critical_alerts > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="font-bold text-red-400">{data.critical_alerts} Kritik Uyari</span>
          </div>
          {data.alerts?.filter(a => a.level === 'critical').map((a, i) => (
            <p key={i} className="text-sm text-red-300 ml-7">{a.message}</p>
          ))}
        </div>
      )}

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <div className="rounded-lg bg-card p-4 text-center">
          <Globe className="w-5 h-5 mx-auto text-green-500 mb-1" />
          <p className="text-2xl font-bold">{sys.active_domains || 0}</p>
          <p className="text-xs text-muted-foreground">Aktif Domain</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Server className="w-5 h-5 mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold">{sys.active_nodes || 0}</p>
          <p className="text-xs text-muted-foreground">Aktif Node</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Activity className="w-5 h-5 mx-auto text-amber-500 mb-1" />
          <p className="text-2xl font-bold">{sys.running_campaigns || 0}</p>
          <p className="text-xs text-muted-foreground">Aktif Kampanya</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Zap className="w-5 h-5 mx-auto text-cyan-500 mb-1" />
          <p className="text-2xl font-bold">{(data?.safe_capacity || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Safe Kapasite</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Shield className="w-5 h-5 mx-auto text-purple-500 mb-1" />
          <p className="text-2xl font-bold">{(data?.projection_30d || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">30d Projeksiyon</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Queue */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Queue Durumu</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2 rounded bg-muted/30">
                <p className="text-xl font-bold">{q.queued || 0}</p>
                <p className="text-[10px] text-muted-foreground">Kuyrukta</p>
              </div>
              <div className="p-2 rounded bg-muted/30">
                <p className="text-xl font-bold">{q.processing || 0}</p>
                <p className="text-[10px] text-muted-foreground">Isleniyor</p>
              </div>
              <div className="p-2 rounded bg-muted/30">
                <p className="text-xl font-bold text-green-500">{q.completed || 0}</p>
                <p className="text-[10px] text-muted-foreground">Tamamlanan</p>
              </div>
              <div className="p-2 rounded bg-muted/30">
                <p className="text-xl font-bold text-amber-500">{q.retry || 0}</p>
                <p className="text-[10px] text-muted-foreground">Retry</p>
              </div>
              <div className="p-2 rounded bg-muted/30">
                <p className="text-xl font-bold text-red-500">{q.dead || 0}</p>
                <p className="text-[10px] text-muted-foreground">Dead Letter</p>
              </div>
              <div className="p-2 rounded bg-muted/30">
                <p className="text-xl font-bold">{q.grand_total || 0}</p>
                <p className="text-[10px] text-muted-foreground">Toplam</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Health */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Sistem Sagligi</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Paused Domains</span><span className={sys.paused_domains > 0 ? 'text-red-400' : ''}>{sys.paused_domains || 0}</span></div>
              <div className="flex justify-between"><span>Dead Nodes</span><span className={sys.dead_nodes > 0 ? 'text-red-400' : ''}>{sys.dead_nodes || 0}</span></div>
              <div className="flex justify-between"><span>Throttled Domains</span><span className={sys.throttled_domains > 0 ? 'text-amber-400' : ''}>{sys.throttled_domains || 0}</span></div>
              <div className="flex justify-between"><span>Suppressed Contacts</span><span>{sys.suppressed_contacts || 0}</span></div>
              <div className="flex justify-between"><span>7d Projeksiyon</span><span className="text-green-400">{(data?.projection_7d || 0).toLocaleString()}/gun</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-base">Canli Uyarilar</CardTitle></CardHeader>
          <CardContent>
            {data?.alerts?.length > 0 ? (
              <div className="space-y-2">
                {data.alerts.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded text-sm ${a.level === 'critical' ? 'bg-red-500/10' : a.level === 'danger' ? 'bg-orange-500/10' : a.level === 'warning' ? 'bg-amber-500/10' : 'bg-blue-500/10'}`}>
                    {a.level === 'critical' ? <XCircle className="w-4 h-4 text-red-500" /> : a.level === 'danger' ? <AlertTriangle className="w-4 h-4 text-orange-500" /> : <CheckCircle className="w-4 h-4 text-blue-500" />}
                    <span>{a.message}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">{a.action}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Sistem saglkli, uyari yok</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
